import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../lib/store';
import { CreditCardIcon, PackageIcon, FileTextIcon, ClockIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
interface PaymentRequest {
  id: string;
  paymentRequestId: string;
  type: 'order' | 'prescription' | 'other' | 'refund';
  referenceId: string;
  createdAt: string;
  amount: number;
  currency: string;
  status: 'pending' | 'paid' | 'expired' | 'cancelled';
}
const basePaymentRequests: PaymentRequest[] = [{
  id: '1',
  paymentRequestId: 'PR-2025-000123',
  type: 'order',
  referenceId: 'ORD-2025-001234',
  createdAt: '2025-01-15T10:30:00Z',
  amount: 450.0,
  currency: 'SAR',
  status: 'pending'
}, {
  id: '2',
  paymentRequestId: 'PR-2025-000122',
  type: 'prescription',
  referenceId: 'RX-2025-000456',
  createdAt: '2025-01-14T14:20:00Z',
  amount: 280.5,
  currency: 'SAR',
  status: 'paid'
}, {
  id: '3',
  paymentRequestId: 'PR-2025-000121',
  type: 'order',
  referenceId: 'ORD-2025-001233',
  createdAt: '2025-01-10T09:15:00Z',
  amount: 125.0,
  currency: 'SAR',
  status: 'expired'
}];
export function PaymentRequestsSection() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const refundRequests = useStore(state => state.refundRequests);
  const paymentRequests: PaymentRequest[] = [...refundRequests.map(request => ({
    ...request,
    type: 'refund' as const
  })), ...basePaymentRequests];
  const filteredRequests = statusFilter === 'all' ? paymentRequests : paymentRequests.filter(req => req.status === statusFilter);
  const getStatusBadge = (status: string) => {
    const badges = {
      pending: {
        bg: 'bg-yellow-100',
        text: 'text-yellow-800',
        icon: ClockIcon,
        label: 'Pending'
      },
      paid: {
        bg: 'bg-green-100',
        text: 'text-green-800',
        icon: CheckCircleIcon,
        label: 'Paid'
      },
      expired: {
        bg: 'bg-gray-100',
        text: 'text-gray-800',
        icon: XCircleIcon,
        label: 'Expired'
      },
      cancelled: {
        bg: 'bg-red-100',
        text: 'text-red-800',
        icon: XCircleIcon,
        label: 'Cancelled'
      }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`} data-id="element-959" data-mp="6r77c7ts433z">
        <Icon className="w-3.5 h-3.5" data-id="element-960" data-mp="fqc4b9pozoe1" />
        {badge.label}
      </span>;
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'order':
        return <PackageIcon className="w-4 h-4" data-id="element-961" data-mp="kncarlqget5o" />;
      case 'prescription':
        return <FileTextIcon className="w-4 h-4" data-id="element-962" data-mp="po00m42y53yb" />;
      default:
        return <CreditCardIcon className="w-4 h-4" data-id="element-963" data-mp="2mhbjq0g3syf7" />;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  if (filteredRequests.length === 0) {
    return <div className="bg-white rounded-xl border border-gray-200 p-8 text-center" data-id="element-964" data-mp="13s7d3hsmvvek">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-965" data-mp="zsos6m0tbkbq">
          <CreditCardIcon className="w-8 h-8 text-gray-400" data-id="element-966" data-mp="ur992zi73nvz" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-967" data-mp="1rj30m2q244g0">
          No Payment Requests
        </h3>
        <p className="text-sm text-gray-500" data-id="element-968" data-mp="1nisvste03k2q">
          {statusFilter === 'all' ? "You don't have any payment requests yet." : `No ${statusFilter} payment requests found.`}
        </p>
      </div>;
  }
  return <div className="space-y-4" data-id="element-969" data-mp="73hvztotz6tn">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 md:p-6" data-id="element-970" data-mp="75chb940l0y9">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-971" data-mp="23n2dido834w">
          <div data-id="element-972" data-mp="1q0bhw5lh2r3x">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-973" data-mp="1q0kzb4sgbpy0">
              Payment Requests
            </h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-974" data-mp="1utnvlkom5dhw">
              View and manage your payment links and pending payments.
            </p>
          </div>
          {/* Status Filter */}
          <div className="flex items-center gap-2" data-id="element-975" data-mp="1m0kgl7ybksl5">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E2B55] focus:border-transparent" data-id="element-976" data-mp="29pmymgwv8ng6">
              <option value="all" data-id="element-977" data-mp="24qaazty67ccj">All Status</option>
              <option value="pending" data-id="element-978" data-mp="24ozca8jl0usa">Pending</option>
              <option value="paid" data-id="element-979" data-mp="ocafqwa9savm">Paid</option>
              <option value="expired" data-id="element-980" data-mp="oaftnen9icpy">Expired</option>
              <option value="cancelled" data-id="element-981" data-mp="tau5l7rzi6t9">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-982" data-mp="lq1av9iyt6vk">
        <table className="w-full" data-id="element-983" data-mp="grzs6ppgb9km">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-984" data-mp="usaldze8h08y">
            <tr data-id="element-985" data-mp="urr5eo858lxk">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-986" data-mp="uq6h3gcf9z85">
                Payment Request
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-987" data-mp="24ert3jynoge">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-988" data-mp="bteryqa78pw6">
                Reference
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-989" data-mp="71wsy7rqkzdu">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-990" data-mp="3ajop7ycwo5hu">
                Amount
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-991" data-mp="1efqzm64wk81a">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-992" data-mp="1o6byluohnekq">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-993" data-mp="vhefjpcgddqx">
            {filteredRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => navigate(`/account/payment-requests/${request.id}`)} data-id="element-994" data-mp="10g97phwb8enp">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-995" data-mp="19iii87rkw813">
                  <div className="text-sm font-medium text-gray-900" data-id="element-996" data-mp="2gtplezyh2tr4">
                    {request.paymentRequestId}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-997" data-mp="98zjf7maphav">
                  <div className="flex items-center gap-2 text-sm text-gray-600" data-id="element-998" data-mp="98hg5r17u2tg">
                    {getTypeIcon(request.type)}
                    <span className="capitalize" data-id="element-999" data-mp="59rulb60xeyq">{request.type}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1000" data-mp="3rn8givi12jn6">
                  <div className="text-sm text-gray-600" data-id="element-1001" data-mp="3rmqd9ewy755r">
                    {request.referenceId}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1002" data-mp="3hp2iwq397zo4">
                  <div className="text-sm text-gray-600" data-id="element-1003" data-mp="3hqx1vyuythpk">
                    {formatDate(request.createdAt)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1004" data-mp="38ow8jd2urft6">
                  <div className="text-sm font-medium text-gray-900" data-id="element-1005" data-mp="1bvud2xn9pget">
                    {request.amount.toFixed(2)} {request.currency}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1006" data-mp="16uoi0t64xh24">
                  {getStatusBadge(request.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-1007" data-mp="kq8irasqcee6">
                  <button onClick={e => {
                e.stopPropagation();
                navigate(`/account/payment-requests/${request.id}`);
              }} className="text-sm font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-1008" data-mp="kpp3y2ydm64d">
                    View Details
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden space-y-3" data-id="element-1009" data-mp="3j9ynrt6bkevm">
        {filteredRequests.map(request => <div key={request.id} onClick={() => navigate(`/account/payment-requests/${request.id}`)} className="bg-white rounded-xl border border-gray-200 p-4 active:bg-gray-50 transition-colors" data-id="element-1010" data-mp="3o7qo9bl159bf">
            <div className="flex items-start justify-between mb-3" data-id="element-1011" data-mp="36xmu6iexej31">
              <div data-id="element-1012" data-mp="36vs6wxg6mevs">
                <div className="text-sm font-semibold text-gray-900 mb-1" data-id="element-1013" data-mp="31ytfwnrteo1t">
                  {request.paymentRequestId}
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-1014" data-mp="3gor6199b1ntn">
                  {getTypeIcon(request.type)}
                  <span className="capitalize" data-id="element-1015" data-mp="3bvodn4g9kkf1">{request.type}</span>
                  <span data-id="element-1016" data-mp="3pncnf131iwsu">•</span>
                  <span data-id="element-1017" data-mp="3pmjepukitx82">{request.referenceId}</span>
                </div>
              </div>
              {getStatusBadge(request.status)}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-gray-100" data-id="element-1018" data-mp="3hfpq9oosht7">
              <div className="text-xs text-gray-500" data-id="element-1019" data-mp="3umxjn3qtsr1v">
                {formatDate(request.createdAt)}
              </div>
              <div className="text-sm font-semibold text-gray-900" data-id="element-1020" data-mp="259bzbdranhlc">
                {request.amount.toFixed(2)} {request.currency}
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}